import useDeleteCustomer from '@/api/mutations/customers/useDeleteCustomer';
import useGetCustomers from '@/api/queries/customers/useGetCustomers';
import { GetCustomersInput, PaginationInput } from '@/gql/graphql';
import BulkUploadModal from '@/ui/components/bulkUploadDownloadModals/BulkUploadModal';
import CreateCustomerModalMutation from '@/ui/components/createCustomerModalMutation/CreateCustomerModalMutation';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import AuthenticatedPageWrapper from '@/ui/pages/pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { useHashFragment } from '@/utils/customHooks';
import { Alert, Button, ManageableItemsList, Modal } from '@payaca/ambrosia';

import { CustomersPermissions } from '@payaca/permissions/customers/customers.permissions';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import QuickbooksLogo from '../../../assets/images/quickbooks.svg';
import XeroLogo from '../../../assets/images/xero.svg';

export interface IProps {
  isCreatingCustomer?: boolean;
}

const ListedCustomersPage: FC<IProps> = (props) => {
  const { isCreatingCustomer = false } = props;
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showBulkUploadModal, toggleBulkUploadModal] =
    useHashFragment('#bulk-upload');
  const [requiresPersistSearchTerm, setRequiresPersistSearchTerm] =
    useState<boolean>(false);
  const [deleteCustomerId, setDeleteCustomerId] = useState<string | null>(null);

  const [pagination, setPagination] = useState<PaginationInput>({
    offset: 0,
    limit: 20,
  });

  const [input, setInput] = useState<GetCustomersInput>({
    searchTerm: null,
  });

  /**
   * Queries
   */
  const {
    customers,
    isLoading,
    refetch: refetchCustomers,
  } = useGetCustomers(pagination, input);

  useEffect(() => {
    if (requiresPersistSearchTerm) {
      setInput((x) => ({ ...x, searchTerm }));
      setRequiresPersistSearchTerm(false);
    }
  }, [requiresPersistSearchTerm, searchTerm]);

  const anyCustomersSynced = useMemo(() => {
    return customers?.items.some(
      (x) =>
        x.integrationSyncStatus?.xero || x.integrationSyncStatus?.quickbooks
    );
  }, [customers]);

  return (
    <AuthenticatedPageWrapper>
      <div className="p-4 pb-6">
        <ManageableItemsList>
          <ManageableItemsList.HeaderBar
            heading="Customers"
            buttons={
              <>
                <PermissionGuard
                  renderIfHasPermissions={[CustomersPermissions.ADD_CUSTOMER]}
                >
                  <Button
                    onClick={() => history.push(`/customers/new`)}
                    size={'sm'}
                  >
                    Create Customer
                  </Button>
                </PermissionGuard>
              </>
            }
          />
          <ManageableItemsList.ActionBar>
            <ManageableItemsList.ActionBar.SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              onChangeTimeout={() => setRequiresPersistSearchTerm(true)}
              onBlur={() => setRequiresPersistSearchTerm(true)}
              changeTimeoutMs={300}
            />
          </ManageableItemsList.ActionBar>
          <ManageableItemsList.Table
            itemActions={[
              {
                label: 'Delete',
                onClick: (item) => {
                  setDeleteCustomerId(item.id);
                },
              },
            ]}
            uniqueKey="id"
            items={customers?.items || []}
            isLoading={isLoading}
            onClickRow={(customer) =>
              history.push(`/customers/${customer.publicId}`)
            }
          >
            <ManageableItemsList.Table.Column
              header="Customer"
              field="name"
              className="max-w-[300px] truncate"
            />
            <ManageableItemsList.Table.Column
              header="Primary Email"
              field="primaryContact"
              render={(primaryContact, customer) => {
                return <span>{primaryContact?.email}</span>;
              }}
            />
            <ManageableItemsList.Table.Column
              header="Primary Phone"
              field="primaryContact"
              render={(primaryContact, customer) => {
                return <span>{primaryContact?.phone}</span>;
              }}
            />
            <ManageableItemsList.Table.Column
              header="Billing Address"
              field="billingAddress"
              className="max-w-[400px] truncate"
              render={(billingAddress) => {
                return <span>{billingAddress?.fullLocalAddress}</span>;
              }}
            />
            {anyCustomersSynced && (
              <ManageableItemsList.Table.Column
                header="Import"
                field="integrationSyncStatus"
                render={(integrationSyncStatus) => {
                  return (
                    <div className="flex flex-row gap-2">
                      {integrationSyncStatus?.xero && (
                        <img alt="Xero" src={XeroLogo} className="h-6 w-6" />
                      )}
                      {integrationSyncStatus?.quickbooks && (
                        <img
                          alt="Quickbooks"
                          src={QuickbooksLogo}
                          className="h-6 w-6"
                        />
                      )}
                    </div>
                  );
                }}
              />
            )}
          </ManageableItemsList.Table>
          <ManageableItemsList.PaginationBar
            type="custom"
            pageSize={customers?.limit || 20}
            currentPage={
              Math.ceil((customers?.offset || 0) / (customers?.limit || 20)) + 1
            }
            totalItems={customers?.totalCount || 0}
            onPageChange={(page) =>
              setPagination((x) => ({
                ...x,
                offset: (page - 1) * (pagination?.limit || 20),
              }))
            }
          />
        </ManageableItemsList>
      </div>
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={toggleBulkUploadModal}
        title="Upload Customers"
        dataType="customers"
      ></BulkUploadModal>
      {deleteCustomerId && (
        <ArchiveCustomerModal
          customerId={deleteCustomerId}
          isOpen={!!deleteCustomerId}
          onClose={() => setDeleteCustomerId(null)}
          onSuccess={() => {
            void refetchCustomers();
          }}
        />
      )}

      <CreateCustomerModalMutation
        isOpen={isCreatingCustomer}
        onClose={() => history.push('/customers')}
        mutationOptions={{
          onSuccess: (data) => {
            history.push(`/customers/${data.createCustomer.publicId}`);
          },
        }}
      />
    </AuthenticatedPageWrapper>
  );
};

export default ListedCustomersPage;

const ArchiveCustomerModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  onSuccess?: () => void;
}> = ({ isOpen, onClose, customerId, onSuccess }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { mutateAsync: mutateDeleteCustomer } = useDeleteCustomer();

  const handleArchive = useCallback(async () => {
    setIsProcessing(true);
    await mutateDeleteCustomer({
      customerId,
    })
      .then((result) => {
        onSuccess?.();

        onClose();
      })
      .catch(() => {
        setErrorMessage('An error occurred while deleting this customer.');
      })
      .finally(() => {
        setIsProcessing(false);
      });
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'onClose', and 'mutateDeleteCustomer'
  }, [customerId, onSuccess]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={'Delete a Customer'}>
      <Modal.Body>
        <p>Are you sure you want to delete this Customer?</p>
      </Modal.Body>
      <Modal.Footer>
        {errorMessage && (
          <Alert colour="red" className="mb-4">
            {errorMessage}
          </Alert>
        )}
        <Modal.Footer.Actions>
          <Button
            colour={'red'}
            isProcessing={isProcessing}
            onClick={() => !isProcessing && handleArchive()}
          >
            {'Yes, delete'}
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};
